import { useMemo, useState } from "react";
import PopupDialog from "src/SharedComponents/PopupDialog/PopupDialog";
import { AuraProductLicenseType, IPaymentProducts } from "src/Utils/Hooks/Payments/useGetPaymentProducts";
import { Annually, Monthly } from "../../LicensePayment";
import PillTabs, { Tab } from "src/SharedComponents/PillTabs/PillTabs";
import { useTranslation } from "react-i18next";
import UpgradePlan from "../UpgradePlan/UpgradePlan";
import { calculateMaxSavings } from "src/Utils/CalculateSavings/CalculateSavings";
import OwnerPermissionNote from "../../OwnerPermissionNote/OwnerPermissionNote";

import '../../PaymentDialog.css';

interface IUpgradeSubscriptionDialog {
    isPermanent?: boolean,
    products: IPaymentProducts;
    onClose: () => void;
    onUpgrade: (priceId: string, packageDescription: string) => void; // Callback for upgrade logic
}

const UpgradeSubscriptionDialog: React.FC<IUpgradeSubscriptionDialog> = ({ products, onClose, onUpgrade, isPermanent}) => {
    const { t } = useTranslation();
    let [currentPriceId, setCurrentPriceId] = useState<string | undefined>(products?.currentPriceId);

    // Determine the current package details
    const currentPackage = useMemo(() => {
        return products?.suiteProductComboInfos?.find(
            (suite) =>
                suite?.monthlyInfo?.priceId === currentPriceId ||
                suite?.yearlyInfo?.priceId === currentPriceId
        );
    }, [products, currentPriceId]);
    const PermanentLicenseStorage = 250;
    const PermanentLicenseSeats = 3;
    // Find the default tab based on the current price ID
    const defaultTab = useMemo(() => {
        if(isPermanent)
        {
            return Monthly;
        }
        return currentPackage?.monthlyInfo?.priceId === currentPriceId ? Monthly : Annually;
    }, [currentPackage, currentPriceId, isPermanent]);

    const [selectedTab, setSelectedTab] = useState<string>(defaultTab);
    
    const tabs: Tab[] = useMemo(() => [
        { title: t(Monthly), disabled: defaultTab === Annually },
        { title: t(Annually), disabled: false }
    ], [t, defaultTab]);

    const handleTabChange = (tab: string) => {
        setSelectedTab(tab);
    };
    
    const eligiblePackages = useMemo(() => {
        const tierPriority: Record<string, number> = {
            SuiteS: 1, // Lowest tier
            SuiteM: 2, // Mid-tier
            SuiteL: 3  // Highest tier
        };
        
    
        if(isPermanent) {
            var currentPriceId = products.suiteProductComboInfos.find(x => x.productType === AuraProductLicenseType.SuiteS)?.monthlyInfo.priceId;
            setCurrentPriceId(currentPriceId)
            if(currentPackage) {
                return products.suiteProductComboInfos.map((suite) => {
                const isCurrentPackage =
                (selectedTab === Monthly && suite.monthlyInfo.priceId === currentPriceId) ||
                (selectedTab === Annually && suite.yearlyInfo.priceId === currentPriceId);
                
                const isUpgrade = tierPriority[suite.productType] >= tierPriority[currentPackage.productType] 
        
                const isDowngrade =
                    // Prevent downgrades to lower tiers
                    tierPriority[suite.productType] < tierPriority[currentPackage.productType];
        
                return {
                    ...suite,
                    isDisabled: isCurrentPackage || isDowngrade || !isUpgrade, // Disable current package, downgrades, or non-upgradeable packages
                    extraSeats: suite.userSeats - PermanentLicenseSeats,
                    extraStorage: suite.storageInGb - PermanentLicenseStorage,
                };
            });
        }
    }
        if (!currentPackage) return [];

        return products.suiteProductComboInfos.map((suite) => {
            const isCurrentPackage =
            (selectedTab === Monthly && suite.monthlyInfo.priceId === currentPriceId) ||
            (selectedTab === Annually && suite.yearlyInfo.priceId === currentPriceId);

            const isUpgrade = tierPriority[suite.productType] >= tierPriority[currentPackage.productType] 
    
            const isDowngrade =
                // Prevent downgrades to lower tiers
                tierPriority[suite.productType] < tierPriority[currentPackage.productType];
    
            return {
                ...suite,
                isDisabled: isCurrentPackage || isDowngrade || !isUpgrade, // Disable current package, downgrades, or non-upgradeable packages
                extraSeats: suite.userSeats - currentPackage.userSeats,
                extraStorage: suite.storageInGb - currentPackage.storageInGb,
            };
        });
    }, [products, currentPackage, selectedTab, isPermanent]);

    const getPackageDetails = (priceId: string): string => {
        const packageInfo = products?.suiteProductComboInfos?.find(
            (suite) =>
                suite?.monthlyInfo?.priceId === priceId ||
                suite?.yearlyInfo?.priceId === priceId
        );
    
        if (packageInfo) {
            if (packageInfo.monthlyInfo?.priceId === priceId) {
                return packageInfo.monthlyInfo?.description;
            } else if (packageInfo.yearlyInfo?.priceId === priceId) {
                return packageInfo.yearlyInfo?.description;
            }
        }
    
        return ''; // Default case if no package found
    };

    const handleUpgradeClick = (priceId: string) => {
        const packageDescription = getPackageDetails(priceId);
        onUpgrade(priceId, packageDescription);
    };

    const maxSavingsPercentage = useMemo(() => calculateMaxSavings(products), [products]);

    return(
        <>
            <PopupDialog onClose={() => onClose()} marginTop="80px" popupWidth="800px" isOutsideClickAllowed={false} surfaceClassName="payment-dialog__surface">
                <div className="p-3">
                    <div className="row" style={{ padding: '0 0 10px 10px' }}>
                        <div className="col-md-10">
                            <span className="payment-dialog__title">
                                {t('ChooseOption')}
                            </span>
                        </div>
                        {isPermanent && <div className="col-md-10">
                            <span style={{ color:'red', fontSize: '14px' }}>
                                <i className="bi bi-exclamation-triangle"></i> {t('PermanentUpgradeWarning')}
                            </span>
                        </div>}
                    </div>
                    <br />
                    <PillTabs
                        tabs={tabs}
                        selectedTab={selectedTab}
                        onTabChange={handleTabChange}
                        bottomTextIndex={1}
                        bottomText={t('SaveUpTo', { percentage: maxSavingsPercentage })}
                    />
                    <div className="row pb-4 pt-5" style={{ margin: 'auto' }}>
                        {eligiblePackages.map((suite) => (
                            <UpgradePlan
                                key={suite.productType}
                                selectedTab={selectedTab}
                                title={AuraProductLicenseType[suite.productType]}
                                currency={suite.currency}
                                userSeats={suite.userSeats}
                                storageInGb={suite.storageInGb}
                                monthlypriceObj={suite.monthlyInfo}
                                yearlypriceObj={suite.yearlyInfo}
                                onPayClick={handleUpgradeClick}
                                disable={
                                    suite.isDisabled ||
                                    (selectedTab === Monthly && suite.monthlyInfo.priceId === currentPriceId) || // Disable current monthly package
                                    (selectedTab === Annually && suite.yearlyInfo.priceId === currentPriceId) // Disable current annual package
                                }
                                isCurrentPackage={
                                    (selectedTab === Monthly && suite.monthlyInfo.priceId === currentPriceId) || // Mark current monthly package
                                    (selectedTab === Annually && suite.yearlyInfo.priceId === currentPriceId) // Mark current annual package
                                }
                                extraSeats={suite.extraSeats}
                                extraStorage={suite.extraStorage}
                            />
                        ))}
                    </div>
                    <div className="payment-dialog__footer">
                        <OwnerPermissionNote />
                        <span className="payment-dialog__contact">
                            {t('FurtherAssistance')}&nbsp;
                            <a
                                href="mailto:office@aurareality.com"
                                className="text-decoration-underline"
                                style={{ color: 'var(--color-primary)' }}
                            >
                                {t('AuraOffice')}
                            </a>
                        </span>
                    </div>
                </div>
            </PopupDialog>
        </>
    );
}

export default UpgradeSubscriptionDialog;